/**
 * storefront.api — the public customer API calls (no login needed).
 * Every catalogue call carries the chosen `college` so results are scoped to it.
 */

import { apiClient } from '@/lib/apiClient';
import { guessEmoji } from '@/utils/emoji';

/**
 * Normalises an API product into the shape the UI components expect:
 *   • `id` (from `_id`) so the cart keys stay consistent
 *   • `emoji` fallback for the image tile until real photos exist
 */
export function normalizeProduct(p) {
  if (!p) return p;
  return {
    ...p,
    id: p._id,
    emoji: guessEmoji(`${p.name} ${p.brand ?? ''} ${p.category?.name ?? ''}`),
    image: p.images?.[0] ?? null,
  };
}

export const storefrontApi = {
  colleges: async () => (await apiClient.get('/colleges')).data.colleges,

  categories: async (college) =>
    (await apiClient.get('/categories', { params: { college } })).data.categories,

  /** Active promo coupons for this campus, shown on the home page. */
  coupons: async (college) =>
    (await apiClient.get('/coupons', { params: { college } })).data.coupons,

  /** What this campus buys most, by units sold across paid orders. */
  topSelling: async (college) =>
    (await apiClient.get('/products/top-selling', { params: { college } })).data.products.map(
      normalizeProduct,
    ),

  /** Hostels/blocks this college delivers to. Active ones only. */
  deliveryLocations: async (college) =>
    (await apiClient.get('/delivery-locations', { params: { college } })).data.locations,

  products: async ({ college, search, category, page, limit }) => {
    const envelope = await apiClient.get('/products', {
      params: { college, search, category, page, limit },
    });
    return {
      ...envelope.data,
      items: envelope.data.items.map(normalizeProduct),
    };
  },

  product: async (slug, college) =>
    normalizeProduct(
      (await apiClient.get(`/products/${slug}`, { params: { college } })).data.product,
    ),
};

export default storefrontApi;
